import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";

// Landing page for the link in the verification email: /verify/:token.
// Confirms the token, refreshes the cached session, then drops the user
// into the app. Works whether or not they're already signed in.
export default function VerifyEmail() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();
  const [state, setState] = useState<"verifying" | "ok" | "error">("verifying");
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // StrictMode double-invoke guard
    ran.current = true;
    (async () => {
      try {
        await api.verifyEmail(token);
        await qc.invalidateQueries({ queryKey: ["me"] });
        setState("ok");
        setTimeout(() => nav("/", { replace: true }), 1200);
      } catch {
        setState("error");
      }
    })();
  }, [token, qc, nav]);

  return (
    <div className="relative grid h-full place-items-center bg-bg p-6 overflow-hidden">
      <div className="auth-glow" aria-hidden="true" />
      <div className="card auth-card max-w-md w-full p-8 space-y-4 relative text-center">
        {state === "verifying" && (
          <>
            <h1 className="text-xl font-semibold">Confirming your email…</h1>
            <p className="text-sm muted">One moment.</p>
          </>
        )}
        {state === "ok" && (
          <>
            <div className="text-3xl">✅</div>
            <h1 className="text-xl font-semibold">Email confirmed</h1>
            <p className="text-sm muted">Taking you to API Stash…</p>
          </>
        )}
        {state === "error" && (
          <>
            <div className="text-3xl">⚠️</div>
            <h1 className="text-xl font-semibold">This link didn't work</h1>
            <p className="text-sm muted">
              The verification link is invalid or has expired. Sign in and request a fresh one.
            </p>
            <button className="btn-primary w-full justify-center" onClick={() => nav("/", { replace: true })}>
              Continue
            </button>
          </>
        )}
      </div>
    </div>
  );
}
